/**
 * API Client for Snowfield.ai Backend
 * Replaces direct client-side API calls with backend API calls
 */

// API base URL - defaults to same origin (works with Vercel)
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Fetch weather data for a location
 * @param {string} locationId - Location ID or slug (e.g., 'dodge-ridge')
 * @param {Object} options - Optional parameters
 * @param {boolean} options.forceRefresh - If true, bypasses backend cache
 * @returns {Promise<Object>} Weather data object
 */
export async function fetchWeatherData(locationId = 'dodge-ridge', options = {}) {
  try {
    const { forceRefresh = false } = options;
    const params = new URLSearchParams();
    if (forceRefresh) {
      params.set('nocache', 'true');
    }
    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/weather/${locationId}${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Failed to fetch weather data:', error);
    throw new Error(`Weather API error: ${error.message}`);
  }
}

/**
 * Fetch all available locations
 * @returns {Promise<Array>} Array of location objects
 */
export async function fetchLocations() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/locations`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    // Ensure we return an array, even if API response is malformed
    return Array.isArray(data.locations) ? data.locations : [];

  } catch (error) {
    console.error('Failed to fetch locations:', error);
    throw new Error(`Locations API error: ${error.message}`);
  }
}

/**
 * Search for nearest locations to coordinates
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @param {number} maxDistance - Maximum distance in miles (default: 100)
 * @returns {Promise<Object>} Search results with nearest locations
 */
export async function searchLocations(lat, lon, maxDistance = 100) {
  try {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lon: lon.toString(),
      maxDistance: maxDistance.toString()
    });

    const response = await fetch(`${API_BASE_URL}/api/locations/search?${params}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Failed to search locations:', error);
    throw new Error(`Location search error: ${error.message}`);
  }
}

/**
 * Fetch crowd prediction data for a location
 * @param {string} locationId - Location ID or slug
 * @param {Object} options - Optional parameters
 * @param {number} options.days - Number of days to forecast (default: 7)
 * @returns {Promise<Object>} Crowd prediction data
 */
export async function fetchCrowdData(locationId = 'dodge-ridge', options = {}) {
  try {
    const { days = 7 } = options;
    const params = new URLSearchParams({ days: days.toString() });
    const url = `${API_BASE_URL}/api/crowd/${locationId}?${params}`;

    const response = await fetch(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Failed to fetch crowd data:', error);
    throw new Error(`Crowd API error: ${error.message}`);
  }
}

/**
 * Fetch comparison summary for all resorts (batch endpoint)
 * Returns compact weather data for all resorts in a single request
 * @returns {Promise<Object>} Object with resorts array and meta info
 */
export async function fetchComparisonSummary() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/comparison/summary`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Failed to fetch comparison summary:', error);
    throw new Error(`Comparison API error: ${error.message}`);
  }
}

/**
 * Check API health
 * @returns {Promise<Object>} Health status
 */
export async function checkHealth() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Health check failed:', error);
    return { status: 'error', error: error.message };
  }
}

/**
 * Fetch advanced snow properties data for a location
 * Includes: avalanche danger, lift status, SLR, crystal formation,
 * preservation, surface evolution, golden hour, aspect rose,
 * wind chill by zone, and ensemble spread
 *
 * @param {string} locationId - Location ID or slug
 * @param {Object} options - Optional parameters
 * @param {boolean} options.forceRefresh - If true, bypasses backend cache
 * @returns {Promise<Object>} Advanced snow data object
 */
export async function fetchAdvancedSnowData(locationId = 'dodge-ridge', options = {}) {
  try {
    const { forceRefresh = false } = options;
    const params = new URLSearchParams();
    if (forceRefresh) {
      params.set('nocache', 'true');
    }
    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/snow/${locationId}${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Failed to fetch advanced snow data:', error);
    throw new Error(`Advanced Snow API error: ${error.message}`);
  }
}

// ============================================
// Authentication API
// ============================================

/**
 * Get current user session
 * @returns {Promise<Object>} Session data with user object if authenticated
 */
export async function getSession() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/session`, {
      credentials: 'include',
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Session check failed:', error);
    return { user: null, authenticated: false };
  }
}

/**
 * Sign up with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} name - User name (optional)
 * @returns {Promise<Object>} User data
 */
export async function signUp(email, password, name) {
  const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, name }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Sign up failed');
  }
  return data;
}

/**
 * Log in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} User data
 */
export async function login(email, password) {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Login failed');
  }
  return data;
}

/**
 * Log out current user
 * @returns {Promise<Object>} Logout confirmation
 */
export async function logout() {
  const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  });

  const data = await response.json();
  return data;
}

/**
 * Get available auth providers
 * @returns {Promise<Object>} Configured providers
 */
export async function getAuthProviders() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/providers`);
    const data = await response.json();
    return data.providers || {};
  } catch (error) {
    console.error('Failed to get auth providers:', error);
    return {};
  }
}

/**
 * Get Google OAuth URL
 * @param {string} redirect - Path to redirect after login
 * @returns {string} Google OAuth URL
 */
export function getGoogleAuthUrl(redirect = '/') {
  return `${API_BASE_URL}/api/auth/google?redirect=${encodeURIComponent(redirect)}`;
}


// ============================================
// Favorites API
// ============================================

/**
 * Get user's favorite locations
 * @returns {Promise<Object>} Favorites list
 */
export async function getFavorites() {
  const response = await fetch(`${API_BASE_URL}/api/favorites`, {
    credentials: 'include',
  });

  if (response.status === 401) {
    return { favorites: [], total: 0 };
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to get favorites');
  }
  return data;
}

/**
 * Add a location to favorites
 * @param {string} locationId - Location ID or slug
 * @returns {Promise<Object>} Added favorite
 */
export async function addFavorite(locationId) {
  const response = await fetch(`${API_BASE_URL}/api/favorites`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ locationId }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to add favorite');
  }
  return data;
}

/**
 * Remove a location from favorites
 * @param {string} locationId - Location ID or slug
 * @returns {Promise<Object>} Removal confirmation
 */
export async function removeFavorite(locationId) {
  const response = await fetch(`${API_BASE_URL}/api/favorites/${locationId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Failed to remove favorite');
  }
  return data;
}
